import { useState, useEffect } from 'react';
import { winchPricingAPI } from '@/api/winchPricing';
import { useAuth } from '@/hooks';
import type { WinchPricingTier, CreatePricingTierData } from '@/types';

interface ServiceTiers {
  serviceId: number;
  serviceName: string;
  tiers: WinchPricingTier[];
}

interface TierFormData {
  minDistanceKm: string;
  maxDistanceKm: string;
  price: string;
  isActive: boolean;
}

/**
 * Winch Pricing Management Component
 * Comprehensive admin interface for managing pricing tiers
 */
export function WinchPricingManagement() {
  // Service IDs for winch services
  const WINCH_SERVICES = [
    { id: 13, name: 'ونش عادي', nameEn: 'Regular Winch', icon: '🚗' },
    { id: 14, name: 'ونش كامل', nameEn: 'Full Down Winch', icon: '🚙' },
  ];

  const { hasRole } = useAuth();
  const isManager = hasRole('manager');

  const [serviceTiers, setServiceTiers] = useState<ServiceTiers[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);

  // Form states
  const [selectedService, setSelectedService] = useState<number>(WINCH_SERVICES[0].id);
  const [editingTier, setEditingTier] = useState<WinchPricingTier | null>(null);
  const [deletingTier, setDeletingTier] = useState<WinchPricingTier | null>(null);
  const [formData, setFormData] = useState<TierFormData>({
    minDistanceKm: '',
    maxDistanceKm: '',
    price: '',
    isActive: true,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [bulkData, setBulkData] = useState<string>('');
  const [bulkServiceId, setBulkServiceId] = useState<number>(WINCH_SERVICES[0].id);

  useEffect(() => {
    loadAllServiceTiers();
  }, []);

  const loadAllServiceTiers = async () => {
    setLoading(true);
    setError(null);

    try {
      const tiersData: ServiceTiers[] = [];

      for (const service of WINCH_SERVICES) {
        try {
          const response = await winchPricingAPI.getPricingTiers(service.id);
          tiersData.push({
            serviceId: service.id,
            serviceName: service.name,
            tiers: response.data.data.tiers,
          });
        } catch (err) {
          console.error(`Failed to load tiers for service ${service.id}:`, err);
        }
      }

      setServiceTiers(tiersData);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.message || 'فشل تحميل بيانات الأسعار');
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const showError = (message: string) => {
    setError(message);
    setTimeout(() => setError(null), 5000);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    const minDist = parseFloat(formData.minDistanceKm);
    const maxDist = formData.maxDistanceKm ? parseFloat(formData.maxDistanceKm) : null;
    const price = parseFloat(formData.price);

    if (!formData.minDistanceKm || isNaN(minDist) || minDist < 0) {
      errors.minDistanceKm = 'يجب إدخال مسافة بداية صحيحة';
    }

    if (formData.maxDistanceKm && (isNaN(maxDist!) || maxDist! < 0)) {
      errors.maxDistanceKm = 'يجب إدخال مسافة نهاية صحيحة';
    }

    if (maxDist !== null && minDist >= maxDist) {
      errors.maxDistanceKm = 'يجب أن تكون مسافة النهاية أكبر من مسافة البداية';
    }

    if (!formData.price || isNaN(price) || price <= 0) {
      errors.price = 'يجب إدخال سعر صحيح أكبر من صفر';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      minDistanceKm: '',
      maxDistanceKm: '',
      price: '',
      isActive: true,
    });
    setFormErrors({});
    setEditingTier(null);
  };

  const handleOpenAddModal = (serviceId: number) => {
    setSelectedService(serviceId);
    resetForm();
    setShowAddModal(true);
  };

  const handleOpenEditModal = (tier: WinchPricingTier) => {
    setEditingTier(tier);
    setFormData({
      minDistanceKm: tier.minDistanceKm.toString(),
      maxDistanceKm: tier.maxDistanceKm ? tier.maxDistanceKm.toString() : '',
      price: tier.price.toString(),
      isActive: tier.isActive,
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  const handleOpenDeleteModal = (tier: WinchPricingTier) => {
    setDeletingTier(tier);
    setShowDeleteModal(true);
  };

  const handleOpenBulkModal = (serviceId: number) => {
    setBulkServiceId(serviceId);
    setBulkData('');
    setShowBulkModal(true);
  };

  const handleCreateTier = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const data: CreatePricingTierData = {
        minDistanceKm: parseFloat(formData.minDistanceKm),
        maxDistanceKm: formData.maxDistanceKm ? parseFloat(formData.maxDistanceKm) : null,
        price: parseFloat(formData.price),
        isActive: formData.isActive,
      };

      await winchPricingAPI.createPricingTier(selectedService, data);
      showSuccess('تم إضافة الفئة السعرية بنجاح');
      setShowAddModal(false);
      resetForm();
      await loadAllServiceTiers();
    } catch (err: any) {
      showError(err.response?.data?.error?.message || 'فشل إضافة الفئة السعرية');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateTier = async () => {
    if (!editingTier || !validateForm()) return;

    setSubmitting(true);
    try {
      const data: CreatePricingTierData = {
        minDistanceKm: parseFloat(formData.minDistanceKm),
        maxDistanceKm: formData.maxDistanceKm ? parseFloat(formData.maxDistanceKm) : null,
        price: parseFloat(formData.price),
        isActive: formData.isActive,
      };

      await winchPricingAPI.updatePricingTier(editingTier.id, data);
      showSuccess('تم تحديث الفئة السعرية بنجاح');
      setShowEditModal(false);
      resetForm();
      await loadAllServiceTiers();
    } catch (err: any) {
      showError(err.response?.data?.error?.message || 'فشل تحديث الفئة السعرية');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTier = async () => {
    if (!deletingTier) return;

    setSubmitting(true);
    try {
      await winchPricingAPI.deletePricingTier(deletingTier.id);
      showSuccess('تم حذف الفئة السعرية بنجاح');
      setShowDeleteModal(false);
      setDeletingTier(null);
      await loadAllServiceTiers();
    } catch (err: any) {
      showError(err.response?.data?.error?.message || 'فشل حذف الفئة السعرية');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (tier: WinchPricingTier) => {
    try {
      await winchPricingAPI.toggleTierStatus(tier.id);
      showSuccess(`تم ${tier.isActive ? 'تعطيل' : 'تفعيل'} الفئة السعرية بنجاح`);
      await loadAllServiceTiers();
    } catch (err: any) {
      showError(err.response?.data?.error?.message || 'فشل تحديث حالة الفئة السعرية');
    }
  };

  const handleBulkUpdate = async () => {
    if (!bulkData.trim()) {
      showError('يرجى إدخال بيانات الفئات بتنسيق JSON');
      return;
    }

    setSubmitting(true);
    try {
      const tiers = JSON.parse(bulkData);
      
      // Validate the data structure
      if (!Array.isArray(tiers)) {
        throw new Error('البيانات يجب أن تكون مصفوفة من الفئات');
      }

      await winchPricingAPI.bulkUpdateTiers(bulkServiceId, { tiers, replace: false });
      showSuccess('تم التحديث الجماعي بنجاح');
      setShowBulkModal(false);
      setBulkData('');
      await loadAllServiceTiers();
    } catch (err: any) {
      if (err instanceof SyntaxError) {
        showError('تنسيق JSON غير صحيح');
      } else {
        showError(err.response?.data?.error?.message || err.message || 'فشل التحديث الجماعي');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleExportTiers = (serviceId: number) => {
    const serviceData = serviceTiers.find(s => s.serviceId === serviceId);
    if (!serviceData) return;

    const exportData = serviceData.tiers.map(tier => ({
      minDistanceKm: tier.minDistanceKm,
      maxDistanceKm: tier.maxDistanceKm,
      price: tier.price,
      isActive: tier.isActive,
    }));

    const jsonString = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${serviceData.serviceName}-pricing-tiers.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showSuccess('تم تصدير الفئات بنجاح');
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6" dir="rtl">
        <div className="card p-12 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-gray-400">جاري تحميل بيانات الأسعار...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6" dir="rtl">
      <div className="card">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">إدارة أسعار خدمة الونش</h2>
          <button
            onClick={loadAllServiceTiers}
            className="px-4 py-2 bg-info text-white rounded-md hover:bg-info/80 transition-colors"
          >
            🔄 تحديث
          </button>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-success/20 border border-success rounded-lg">
            <p className="text-success font-medium">✓ {successMessage}</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-danger/20 border border-danger rounded-lg">
            <p className="text-danger font-medium">⚠️ {error}</p>
          </div>
        )}

        {/* Service Pricing Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {serviceTiers.map((serviceData) => {
            const activeTiers = serviceData.tiers.filter((t) => t.isActive);
            const priceRange = activeTiers.length > 0
              ? {
                  min: Math.min(...activeTiers.map((t) => t.price)),
                  max: Math.max(...activeTiers.map((t) => t.price)),
                }
              : null;

            const serviceIcon = WINCH_SERVICES.find(s => s.id === serviceData.serviceId)?.icon || '🚗';

            return (
              <div key={serviceData.serviceId} className="border border-gray-700 rounded-lg overflow-hidden">
                <div className="bg-gradient-to-r from-primary/30 to-primary/20 px-6 py-4">
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <span>{serviceIcon}</span>
                    <span>{serviceData.serviceName}</span>
                  </h3>
                </div>
                <div className="p-6 bg-dark-lighter">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-400 font-medium">إجمالي الفئات</p>
                      <p className="text-2xl font-bold text-white">{serviceData.tiers.length}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400 font-medium">الفئات النشطة</p>
                      <p className="text-2xl font-bold text-success">{activeTiers.length}</p>
                    </div>
                  </div>
                  {priceRange && (
                    <div className="mb-4">
                      <p className="text-sm text-gray-400 font-medium mb-1">نطاق السعر</p>
                      <p className="text-lg font-semibold text-gray-300" dir="ltr">
                        {priceRange.min} - {priceRange.max} KWD
                      </p>
                    </div>
                  )}
                  {isManager && (
                    <div className="space-y-2">
                      <button
                        onClick={() => handleOpenAddModal(serviceData.serviceId)}
                        className="w-full px-4 py-2 bg-primary text-dark rounded-md hover:bg-primary/90 transition-colors font-semibold"
                      >
                        + إضافة فئة سعر جديدة
                      </button>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => handleOpenBulkModal(serviceData.serviceId)}
                          className="px-3 py-2 bg-info/20 text-info border border-info/30 rounded-md hover:bg-info/30 transition-colors text-sm font-semibold"
                        >
                          📥 تحديث جماعي
                        </button>
                        <button
                          onClick={() => handleExportTiers(serviceData.serviceId)}
                          className="px-3 py-2 bg-success/20 text-success border border-success/30 rounded-md hover:bg-success/30 transition-colors text-sm font-semibold"
                        >
                          📤 تصدير
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Detailed Pricing Tables */}
        {serviceTiers.map((serviceData) => {
          const serviceIcon = WINCH_SERVICES.find(s => s.id === serviceData.serviceId)?.icon || '🚗';
          
          return (
            <div key={serviceData.serviceId} className="mb-8">
              <h3 className="text-xl font-bold mb-4 text-white flex items-center gap-2">
                <span>{serviceIcon}</span>
                <span>{serviceData.serviceName} - فئات الأسعار</span>
              </h3>

              {serviceData.tiers.length > 0 ? (
                <div className="overflow-x-auto border border-gray-700 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-700">
                    <thead className="bg-dark-lighter">
                      <tr>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-primary uppercase tracking-wider">
                          المعرف
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-primary uppercase tracking-wider">
                          نطاق المسافة (كم)
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-primary uppercase tracking-wider">
                          السعر
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-primary uppercase tracking-wider">
                          الحالة
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-primary uppercase tracking-wider">
                          تاريخ الإنشاء
                        </th>
                        {isManager && (
                          <th className="px-6 py-3 text-right text-xs font-semibold text-primary uppercase tracking-wider">
                            الإجراءات
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-dark divide-y divide-gray-700">
                      {serviceData.tiers.map((tier) => (
                        <tr key={tier.id} className="hover:bg-dark-lighter transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-mono text-gray-400">#{tier.id}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-white" dir="ltr">
                              {tier.minDistanceKm} - {tier.maxDistanceKm || '∞'} km
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-bold text-primary" dir="ltr">
                              {tier.price} KWD
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {isManager ? (
                              <button
                                onClick={() => handleToggleStatus(tier)}
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
                                  tier.isActive
                                    ? 'bg-success/20 text-success border border-success/30 hover:bg-success/30'
                                    : 'bg-gray-700 text-gray-400 border border-gray-600 hover:bg-gray-600'
                                }`}
                              >
                                {tier.isActive ? '✓ نشط' : '✗ غير نشط'}
                              </button>
                            ) : (
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  tier.isActive
                                    ? 'bg-success/20 text-success border border-success/30'
                                    : 'bg-gray-700 text-gray-400 border border-gray-600'
                                }`}
                              >
                                {tier.isActive ? '✓ نشط' : '✗ غير نشط'}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-xs text-gray-400">
                              {new Date(tier.createdAt).toLocaleDateString('ar-EG')}
                            </div>
                          </td>
                          {isManager && (
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleOpenEditModal(tier)}
                                  className="px-3 py-1 bg-info text-white rounded text-xs hover:bg-info/80 transition-colors"
                                  title="تعديل"
                                >
                                  ✏️ تعديل
                                </button>
                                <button
                                  onClick={() => handleOpenDeleteModal(tier)}
                                  className="px-3 py-1 bg-danger text-white rounded text-xs hover:bg-danger/80 transition-colors"
                                  title="حذف"
                                >
                                  🗑️ حذف
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 bg-dark-lighter rounded-lg border border-gray-700">
                  <p className="text-gray-400">لا توجد فئات أسعار متاحة لهذه الخدمة.</p>
                </div>
              )}
            </div>
          );
        })}

        {/* Info Section */}
        <div className="mt-8 bg-info/10 rounded-lg p-6 border border-info/30">
          <h3 className="text-lg font-bold mb-3 text-info">ℹ️ معلومات نظام التسعير</h3>
          <div className="space-y-2 text-sm text-gray-300">
            <p><strong className="text-primary">التسعير حسب المسافة:</strong> يتم حساب الأسعار بناءً على فئات المسافة</p>
            <p><strong className="text-primary">نوعان من الخدمات:</strong> ونش عادي وونش كامل بأسعار مختلفة</p>
            <p><strong className="text-primary">الحالة النشطة:</strong> يتم استخدام الفئات النشطة فقط لحساب السعر</p>
            <p><strong className="text-primary">الاختيار التلقائي:</strong> يختار النظام تلقائياً الفئة المناسبة بناءً على المسافة</p>
          </div>
        </div>
      </div>

      {/* Add Tier Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-dark border border-gray-700 rounded-lg max-w-md w-full p-6" dir="rtl">
            <h3 className="text-xl font-bold text-white mb-4">إضافة فئة سعر جديدة</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  الخدمة
                </label>
                <div className="px-4 py-2 bg-dark-lighter border border-gray-700 rounded-lg text-white">
                  {WINCH_SERVICES.find(s => s.id === selectedService)?.name}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  مسافة البداية (كم) *
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={formData.minDistanceKm}
                  onChange={(e) => setFormData({ ...formData, minDistanceKm: e.target.value })}
                  className={`w-full px-4 py-2 bg-dark-lighter border ${
                    formErrors.minDistanceKm ? 'border-danger' : 'border-gray-700'
                  } rounded-lg text-white focus:ring-2 focus:ring-primary focus:border-primary`}
                  dir="ltr"
                />
                {formErrors.minDistanceKm && (
                  <p className="mt-1 text-xs text-danger">{formErrors.minDistanceKm}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  مسافة النهاية (كم) - اتركها فارغة لـ ∞
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={formData.maxDistanceKm}
                  onChange={(e) => setFormData({ ...formData, maxDistanceKm: e.target.value })}
                  className={`w-full px-4 py-2 bg-dark-lighter border ${
                    formErrors.maxDistanceKm ? 'border-danger' : 'border-gray-700'
                  } rounded-lg text-white focus:ring-2 focus:ring-primary focus:border-primary`}
                  dir="ltr"
                />
                {formErrors.maxDistanceKm && (
                  <p className="mt-1 text-xs text-danger">{formErrors.maxDistanceKm}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  السعر (دينار كويتي) *
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className={`w-full px-4 py-2 bg-dark-lighter border ${
                    formErrors.price ? 'border-danger' : 'border-gray-700'
                  } rounded-lg text-white focus:ring-2 focus:ring-primary focus:border-primary`}
                  dir="ltr"
                />
                {formErrors.price && (
                  <p className="mt-1 text-xs text-danger">{formErrors.price}</p>
                )}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-4 h-4 text-primary bg-dark-lighter border-gray-700 rounded focus:ring-primary"
                />
                <label htmlFor="isActive" className="mr-2 text-sm text-gray-300">
                  نشط
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleCreateTier}
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-primary text-dark rounded-md hover:bg-primary/90 transition-colors font-semibold disabled:opacity-50"
              >
                {submitting ? 'جاري الإضافة...' : 'إضافة'}
              </button>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-gray-700 text-gray-300 rounded-md hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Tier Modal */}
      {showEditModal && editingTier && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-dark border border-gray-700 rounded-lg max-w-md w-full p-6" dir="rtl">
            <h3 className="text-xl font-bold text-white mb-4">تعديل فئة السعر #{editingTier.id}</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  مسافة البداية (كم) *
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={formData.minDistanceKm}
                  onChange={(e) => setFormData({ ...formData, minDistanceKm: e.target.value })}
                  className={`w-full px-4 py-2 bg-dark-lighter border ${
                    formErrors.minDistanceKm ? 'border-danger' : 'border-gray-700'
                  } rounded-lg text-white focus:ring-2 focus:ring-primary focus:border-primary`}
                  dir="ltr"
                />
                {formErrors.minDistanceKm && (
                  <p className="mt-1 text-xs text-danger">{formErrors.minDistanceKm}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  مسافة النهاية (كم) - اتركها فارغة لـ ∞
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={formData.maxDistanceKm}
                  onChange={(e) => setFormData({ ...formData, maxDistanceKm: e.target.value })}
                  className={`w-full px-4 py-2 bg-dark-lighter border ${
                    formErrors.maxDistanceKm ? 'border-danger' : 'border-gray-700'
                  } rounded-lg text-white focus:ring-2 focus:ring-primary focus:border-primary`}
                  dir="ltr"
                />
                {formErrors.maxDistanceKm && (
                  <p className="mt-1 text-xs text-danger">{formErrors.maxDistanceKm}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  السعر (دينار كويتي) *
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className={`w-full px-4 py-2 bg-dark-lighter border ${
                    formErrors.price ? 'border-danger' : 'border-gray-700'
                  } rounded-lg text-white focus:ring-2 focus:ring-primary focus:border-primary`}
                  dir="ltr"
                />
                {formErrors.price && (
                  <p className="mt-1 text-xs text-danger">{formErrors.price}</p>
                )}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActiveEdit"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-4 h-4 text-primary bg-dark-lighter border-gray-700 rounded focus:ring-primary"
                />
                <label htmlFor="isActiveEdit" className="mr-2 text-sm text-gray-300">
                  نشط
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleUpdateTier}
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-primary text-dark rounded-md hover:bg-primary/90 transition-colors font-semibold disabled:opacity-50"
              >
                {submitting ? 'جاري التحديث...' : 'تحديث'}
              </button>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-gray-700 text-gray-300 rounded-md hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingTier && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-dark border border-gray-700 rounded-lg max-w-md w-full p-6" dir="rtl">
            <h3 className="text-xl font-bold text-white mb-4">تأكيد الحذف</h3>
            
            <p className="text-gray-300 mb-6">
              هل أنت متأكد من حذف هذه الفئة السعرية؟
            </p>

            <div className="bg-dark-lighter p-4 rounded-lg mb-6">
              <p className="text-sm text-gray-400">نطاق المسافة:</p>
              <p className="text-white font-semibold" dir="ltr">
                {deletingTier.minDistanceKm} - {deletingTier.maxDistanceKm || '∞'} km
              </p>
              <p className="text-sm text-gray-400 mt-2">السعر:</p>
              <p className="text-primary font-bold" dir="ltr">{deletingTier.price} KWD</p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleDeleteTier}
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-danger text-white rounded-md hover:bg-danger/90 transition-colors font-semibold disabled:opacity-50"
              >
                {submitting ? 'جاري الحذف...' : 'حذف'}
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingTier(null);
                }}
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-gray-700 text-gray-300 rounded-md hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Update Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-dark border border-gray-700 rounded-lg max-w-2xl w-full p-6" dir="rtl">
            <h3 className="text-xl font-bold text-white mb-4">تحديث جماعي للفئات السعرية</h3>
            
            <div className="mb-4">
              <p className="text-gray-300 text-sm mb-2">
                الخدمة: <strong className="text-white">{WINCH_SERVICES.find(s => s.id === bulkServiceId)?.name}</strong>
              </p>
              <p className="text-gray-400 text-xs mb-4">
                أدخل مصفوفة JSON من الفئات السعرية. كل فئة يجب أن تحتوي على: minDistanceKm، maxDistanceKm (أو null)، price، isActive
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                بيانات JSON
              </label>
              <textarea
                value={bulkData}
                onChange={(e) => setBulkData(e.target.value)}
                rows={12}
                className="w-full px-4 py-2 bg-dark-lighter border border-gray-700 rounded-lg text-white font-mono text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder={`[\n  {\n    "minDistanceKm": 0,\n    "maxDistanceKm": 10,\n    "price": 5.0,\n    "isActive": true\n  },\n  {\n    "minDistanceKm": 10,\n    "maxDistanceKm": 20,\n    "price": 10.0,\n    "isActive": true\n  }\n]`}
                dir="ltr"
              />
            </div>

            <div className="bg-info/10 border border-info/30 rounded-lg p-4 mb-4">
              <p className="text-info text-sm">
                <strong>ملاحظة:</strong> سيتم إضافة الفئات الجديدة دون حذف الفئات الموجودة. للاستبدال الكامل، استخدم واجهة برمجة التطبيقات مباشرة.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleBulkUpdate}
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-primary text-dark rounded-md hover:bg-primary/90 transition-colors font-semibold disabled:opacity-50"
              >
                {submitting ? 'جاري التحديث...' : 'تحديث'}
              </button>
              <button
                onClick={() => {
                  setShowBulkModal(false);
                  setBulkData('');
                }}
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-gray-700 text-gray-300 rounded-md hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
